import { type JSX } from 'react';
import { Box, Button, ButtonBase, Typography, useTheme } from '@mui/material';
import { useLocale } from '@repo/i18n';
import {
    mergeMemberCardLine,
    removeMemberCardSubGroup,
    splitMemberCardLine,
} from 'src/state/native-membership/nativeMemberCoverageAtom';
import MemberCardCoveragePill from './MemberCardCoveragePill';

type MemberCardSplitEditorProps = {
    lineKey: string;
    quantity: number;
    sizes: number[];
};

// Sub-group editor for a split ticket category (the widget twin of POS's TicketSplitEditor): one row per
// sub-group — a stepper for its ticket count + the coverage pill (`${lineKey}:gN`) steering its card.
// A ghost line notes any tickets still on auto. The atom owns split/merge/remove + re-keying.
export default function MemberCardSplitEditor({
    lineKey,
    quantity,
    sizes,
}: MemberCardSplitEditorProps): JSX.Element {
    const { t } = useLocale();
    const theme = useTheme();

    const assigned = sizes.reduce((sum, n) => sum + n, 0);
    const remaining = Math.max(0, quantity - assigned);

    const setSize = (index: number, next: number) => {
        const others = assigned - sizes[index];
        const capped = Math.max(1, Math.min(next, quantity - others));
        if (capped === sizes[index]) return;
        splitMemberCardLine(
            lineKey,
            sizes.map((size, i) => (i === index ? capped : size)),
        );
    };

    const addGroup = () => {
        if (remaining > 0) splitMemberCardLine(lineKey, [...sizes, remaining]);
    };

    return (
        <Box sx={{ mt: 0.5, pl: 1.5, borderLeft: `2px solid ${theme.palette.divider}` }}>
            {sizes.map((size, index) => (
                <Box key={`${lineKey}:g${index}`} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ButtonBase
                            aria-label={t.remove_group}
                            onClick={() => removeMemberCardSubGroup(lineKey, index)}
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                color: theme.palette.text.secondary,
                                fontSize: 18,
                                lineHeight: 1,
                            }}
                        >
                            ×
                        </ButtonBase>
                        <Typography variant="body2" sx={{ minWidth: 72 }}>
                            {t.group_label} {index + 1}
                        </Typography>
                        <Stepper
                            value={size}
                            min={1}
                            max={size + remaining}
                            onChange={(value) => setSize(index, value)}
                        />
                    </Box>
                    <Box sx={{ pl: 4.5 }}>
                        <MemberCardCoveragePill lineKey={`${lineKey}:g${index}`} />
                    </Box>
                </Box>
            ))}

            {remaining > 0 && (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', pl: 4.5, mb: 1, color: theme.palette.text.secondary }}
                >
                    ↳ {t.remaining_on_auto}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    onClick={addGroup}
                    disabled={remaining <= 0}
                    sx={{ textTransform: 'none', minHeight: 36 }}
                >
                    + {t.add_group}
                </Button>
                <Button
                    size="small"
                    onClick={() => mergeMemberCardLine(lineKey)}
                    sx={{
                        textTransform: 'none',
                        minHeight: 36,
                        color: theme.palette.text.secondary,
                    }}
                >
                    {t.merge_back_to_auto}
                </Button>
            </Box>
        </Box>
    );
}

function Stepper(props: {
    value: number;
    min: number;
    max: number;
    onChange: (value: number) => void;
}): JSX.Element {
    const theme = useTheme();
    const { value, min, max, onChange } = props;
    const btnSx = {
        width: 32,
        height: 32,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        fontSize: 18,
        lineHeight: 1,
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ButtonBase
                aria-label="-"
                disabled={value <= min}
                onClick={() => onChange(value - 1)}
                sx={{ ...btnSx, opacity: value <= min ? 0.4 : 1 }}
            >
                −
            </ButtonBase>
            <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>
                {value}
            </Typography>
            <ButtonBase
                aria-label="+"
                disabled={value >= max}
                onClick={() => onChange(value + 1)}
                sx={{ ...btnSx, opacity: value >= max ? 0.4 : 1 }}
            >
                +
            </ButtonBase>
        </Box>
    );
}
